import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Loader2, FileText, Calendar, Eye, MousePointerClick, X as XIcon,
  Search, Download, Printer,
} from 'lucide-react';
import ProofOfValuePrint from './reports/ProofOfValuePrint';

interface ExpiredCampaign {
  id: string;
  slug: string;
  brand_name: string;
  placement: string;
  headline: string | null;
  image_url: string | null;
  logo_url: string | null;
  background_color: string | null;
  starts_at: string;
  ends_at: string;
  is_house_ad: boolean;
  invoice_status: string | null;
  invoice_amount_xcd: number | null;
  active_days: number;
  impressions: number;
  clicks: number;
  dismissals: number;
  unique_users: number;
  click_through_rate: number | null;
}

const PLACEMENT_LABELS: Record<string, string> = {
  passenger_home_header: 'Home Header',
  passenger_sheet_idle_carousel: 'Sheet: Where To',
  passenger_sheet_confirm_carousel: 'Sheet: Confirm Ride',
  passenger_sheet_active_ride_carousel: 'Sheet: Active Ride',
  commute_morning_takeover: 'Morning Banner',
  commute_evening_takeover: 'Evening Banner',
  ride_complete_full: 'Ride-Complete',
  weather_alert_banner: 'Weather Alert',
  map_pin_featured: 'Map Pin',
  festival_event_takeover: 'Festival Takeover',
  driver_idle_strip: 'Driver Idle Strip',
  splash_takeover: 'Splash Takeover',
  house_inbound_lead: 'House Lead',
};

function fmtDate(iso: string) {
  if (!iso) return '–';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export default function Reports() {
  const { seesMoney } = useAdminAuth();
  const [rows, setRows] = useState<ExpiredCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [daysBack, setDaysBack] = useState(180);
  const [query, setQuery] = useState('');
  const [printingId, setPrintingId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: rpcErr } = await supabase.rpc(
        'admin_list_expired_campaigns_with_stats',
        { p_days_back: daysBack, p_limit: 200 },
      );
      if (rpcErr) throw rpcErr;
      const blob = data as any;
      if (Array.isArray(blob)) {
        setRows(blob as ExpiredCampaign[]);
      } else if (blob?.ok === false) {
        throw new Error(blob.error || 'forbidden');
      } else {
        setRows([]);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load reports');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daysBack]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.brand_name?.toLowerCase().includes(q) ||
        r.placement?.toLowerCase().includes(q) ||
        r.headline?.toLowerCase().includes(q),
    );
  }, [rows, query]);

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        acc.imp += r.impressions || 0;
        acc.clk += r.clicks || 0;
        acc.cmp += 1;
        acc.inv += Number(r.invoice_amount_xcd) || 0;
        return acc;
      },
      { imp: 0, clk: 0, cmp: 0, inv: 0 },
    );
  }, [rows]);

  return (
    <div className="space-y-6">
      {/* Print-only view of the active PoV (renders into a portal-like overlay) */}
      {printingId && (
        <ProofOfValuePrint
          campaignId={printingId}
          onClose={() => setPrintingId(null)}
        />
      )}

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white">Campaign Reports</h1>
          <p className="text-sm text-gray-400 mt-1">
            Expired campaigns with rolled-up impressions and clicks. Generate a
            Proof of Value summary to attach to renewal invoices.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={daysBack}
            onChange={(e) => setDaysBack(Number(e.target.value))}
            className="bg-gray-900 border border-gray-700 text-white text-sm rounded-md px-3 py-1.5 focus:border-emerald-500"
          >
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 180 days</option>
            <option value={365}>Last 365 days</option>
          </select>
          <Button
            variant="outline"
            size="sm"
            className="border-gray-700 text-gray-300"
            onClick={load}
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Stat strip */}
      <div className={`grid grid-cols-2 gap-3 ${seesMoney ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
        <StatCard
          label="Expired campaigns"
          value={totals.cmp.toString()}
          icon={<FileText className="w-4 h-4 text-cyan-400" />}
        />
        <StatCard
          label="Total impressions"
          value={totals.imp.toLocaleString()}
          icon={<Eye className="w-4 h-4 text-emerald-400" />}
        />
        <StatCard
          label="Total clicks"
          value={totals.clk.toLocaleString()}
          icon={<MousePointerClick className="w-4 h-4 text-amber-400" />}
        />
        {seesMoney && (
          <StatCard
            label="Invoiced"
            value={`$${totals.inv.toLocaleString()} XCD`}
            icon={<Download className="w-4 h-4 text-rose-400" />}
          />
        )}
      </div>

      {/* Filter bar */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by brand, headline, or placement…"
          className="pl-9 bg-gray-900 border-gray-700 text-white placeholder:text-gray-500"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-white"
            aria-label="Clear search"
          >
            <XIcon className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-gray-900/40 border border-gray-800 rounded-xl overflow-hidden">
        {loading && (
          <div className="flex items-center justify-center py-12 text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading reports…
          </div>
        )}
        {!loading && error && (
          <div className="p-6 text-center">
            <p className="text-sm text-red-400">Failed to load: {error}</p>
            <p className="text-xs text-gray-500 mt-2">
              Make sure migration{' '}
              <code className="font-mono text-cyan-400">
                20260620530000_admin_campaign_proof_of_value
              </code>{' '}
              has been applied.
            </p>
          </div>
        )}
        {!loading && !error && filtered.length === 0 && (
          <div className="p-12 text-center text-gray-500 text-sm">
            No expired campaigns in this window.{' '}
            {daysBack < 365 && (
              <button
                type="button"
                onClick={() => setDaysBack(365)}
                className="underline hover:text-emerald-400"
              >
                Try the last 365 days?
              </button>
            )}
          </div>
        )}
        {!loading && !error && filtered.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 uppercase tracking-wider border-b border-gray-800">
                  <th className="px-4 py-3 font-medium">Campaign</th>
                  <th className="px-4 py-3 font-medium">Placement</th>
                  <th className="px-4 py-3 font-medium">Window</th>
                  <th className="px-4 py-3 font-medium text-right">Days</th>
                  <th className="px-4 py-3 font-medium text-right">Impressions</th>
                  <th className="px-4 py-3 font-medium text-right">Clicks</th>
                  <th className="px-4 py-3 font-medium text-right">CTR</th>
                  {seesMoney && <th className="px-4 py-3 font-medium text-right">Invoice</th>}
                  <th className="px-4 py-3 font-medium" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr
                    key={r.id}
                    className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        {r.logo_url ? (
                          <img
                            src={r.logo_url}
                            alt=""
                            className="w-9 h-9 rounded-full border border-gray-700 object-cover shrink-0"
                          />
                        ) : (
                          <div
                            className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                            style={{
                              backgroundColor: r.background_color || '#0f3d2e',
                            }}
                          >
                            <span className="text-xs font-bold text-white">
                              {r.brand_name?.charAt(0) || '?'}
                            </span>
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="text-white font-medium truncate">
                            {r.brand_name}
                          </p>
                          {r.headline && (
                            <p className="text-[11px] text-gray-500 truncate max-w-[220px]">
                              {r.headline}
                            </p>
                          )}
                          {r.is_house_ad && (
                            <Badge
                              variant="outline"
                              className="mt-0.5 text-[9px] border-gray-700 text-gray-500"
                            >
                              House
                            </Badge>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-300">
                      <Badge
                        variant="outline"
                        className="text-[10px] border-gray-700 text-gray-300"
                      >
                        {PLACEMENT_LABELS[r.placement] || r.placement}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-400">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="w-3 h-3" />
                        <span>
                          {fmtDate(r.starts_at)} → {fmtDate(r.ends_at)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-300 font-mono text-xs">
                      {r.active_days}
                    </td>
                    <td className="px-4 py-3 text-right text-emerald-400 font-mono">
                      {r.impressions.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right text-amber-400 font-mono">
                      {r.clicks.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-300 font-mono text-xs">
                      {r.click_through_rate !== null
                        ? `${r.click_through_rate.toFixed(2)}%`
                        : '–'}
                    </td>
                    {seesMoney && <td className="px-4 py-3 text-right text-xs">
                      {r.invoice_amount_xcd ? (
                        <div>
                          <div className="text-white font-mono">
                            ${r.invoice_amount_xcd}
                          </div>
                          <div
                            className={`text-[10px] capitalize ${
                              r.invoice_status === 'paid'
                                ? 'text-emerald-400'
                                : r.invoice_status === 'invoiced'
                                  ? 'text-amber-400'
                                  : 'text-gray-500'
                            }`}
                          >
                            {r.invoice_status || '–'}
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-600">–</span>
                      )}
                    </td>}
                    <td className="px-4 py-3 text-right">
                      <Button
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs h-8"
                        onClick={() => setPrintingId(r.id)}
                      >
                        <Printer className="w-3 h-3 mr-1.5" />
                        Proof of Value
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
}) {
  return (
    <div className="bg-gray-900/40 border border-gray-800 rounded-xl p-4">
      <div className="flex items-center gap-2 text-xs text-gray-500 mb-1.5">
        {icon} {label}
      </div>
      <div className="text-2xl font-bold text-white">{value}</div>
    </div>
  );
}
